import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Truck, Package, Clock, CheckCircle, AlertCircle, 
  Loader2, ArrowRight, MapPin, RefreshCw
} from "lucide-react";
import api from "@/utils/api";

const STATUS_CONFIG = {
  pending: { color: "bg-yellow-100 text-yellow-800", label: "Pending" },
  assigned: { color: "bg-blue-100 text-blue-800", label: "Driver Assigned" },
  in_transit: { color: "bg-purple-100 text-purple-800", label: "In Transit" },
  delivered: { color: "bg-emerald-100 text-emerald-800", label: "Delivered" },
  failed: { color: "bg-red-100 text-red-800", label: "Failed" },
  cancelled: { color: "bg-gray-100 text-gray-800", label: "Cancelled" }
};

const DeliveryCard = ({ delivery, onClick }) => {
  const statusConfig = STATUS_CONFIG[delivery.status] || STATUS_CONFIG.pending;
  
  return (
    <Card 
      className="p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
      data-testid={`delivery-card-${delivery.delivery_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Truck className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold">{delivery.listing_title}</p>
            <p className="text-sm text-muted-foreground">
              {new Date(delivery.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3" /> From
          </p>
          <p className="font-medium">{delivery.pickup_location?.name || delivery.seller_name}</p>
        </div>
        <div>
          <p className="text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3" /> To
          </p>
          <p className="font-medium">
            {delivery.dropoff_location?.city}, {delivery.dropoff_location?.state}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t">
        <div className="flex items-center gap-4">
          <span className="text-sm">
            <span className="text-muted-foreground">Cost:</span>{" "}
            <span className="font-semibold text-emerald-600">${delivery.cost?.toFixed(2)}</span>
          </span>
          <span className="text-sm">
            <span className="text-muted-foreground">Distance:</span>{" "}
            <span className="font-medium">{delivery.distance_km} km</span>
          </span>
        </div>
        <Button variant="ghost" size="sm" className="text-emerald-600">
          Track <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {delivery.estimated_arrival && delivery.status !== 'delivered' && delivery.status !== 'failed' && (
        <div className="mt-3 p-2 bg-emerald-50 rounded-lg flex items-center gap-2">
          <Clock className="h-4 w-4 text-emerald-600" />
          <span className="text-sm text-emerald-700">
            ETA: {new Date(delivery.estimated_arrival).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
          </span>
        </div>
      )}
    </Card>
  );
};

const MyDeliveriesPage = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("buyer");
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDeliveries = async (showLoader = true) => {
    if (!user) {
      navigate("/");
      return;
    }
    
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const res = await api.get(`/delivery/my-deliveries?role=${activeTab}`);
      setDeliveries(res.data.deliveries || []);
    } catch (error) {
      toast.error("Failed to load deliveries");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDeliveries();
  }, [activeTab, user]);

  if (!user) {
    return (
      <div className="min-h-screen pt-52 pb-16">
        <div className="container mx-auto px-4 text-center">
          <Truck className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Login Required</h1>
          <p className="text-muted-foreground mb-6">Please login to view your deliveries.</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  const activeDeliveries = deliveries.filter(d => !['delivered', 'failed', 'cancelled'].includes(d.status));
  const completedDeliveries = deliveries.filter(d => ['delivered', 'failed', 'cancelled'].includes(d.status));

  return (
    <div className="min-h-screen pt-52 pb-16" data-testid="my-deliveries-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold font-outfit">My Deliveries</h1>
            <p className="text-muted-foreground">Track your same-day local deliveries</p>
          </div>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => fetchDeliveries(false)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Tabs for Buyer/Seller view */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-2 max-w-xs">
            <TabsTrigger value="buyer">As Buyer</TabsTrigger>
            <TabsTrigger value="seller">As Seller</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : deliveries.length === 0 ? (
          <Card className="p-12 text-center bg-slate-50 border-dashed">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No Deliveries Yet</h3>
            <p className="text-muted-foreground mb-4">
              {activeTab === "buyer" 
                ? "You haven't requested any same-day deliveries yet."
                : "No buyers have requested same-day delivery for your items yet."}
            </p>
            {activeTab === "buyer" && (
              <Button onClick={() => navigate("/browse")}>Browse Items</Button>
            )}
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Active Deliveries */}
            {activeDeliveries.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Truck className="h-5 w-5 text-emerald-500" />
                  Active Deliveries ({activeDeliveries.length})
                </h2>
                <div className="space-y-4">
                  {activeDeliveries.map(delivery => (
                    <DeliveryCard 
                      key={delivery.delivery_id}
                      delivery={delivery}
                      onClick={() => navigate(`/delivery/track/${delivery.delivery_id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Completed Deliveries */}
            {completedDeliveries.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-gray-500" />
                  Past Deliveries ({completedDeliveries.length})
                </h2>
                <div className="space-y-4">
                  {completedDeliveries.map(delivery => (
                    <DeliveryCard 
                      key={delivery.delivery_id}
                      delivery={delivery}
                      onClick={() => navigate(`/delivery/track/${delivery.delivery_id}`)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDeliveriesPage;
